import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import clsx from "clsx";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import SearchIcon from "@material-ui/icons/Search";
import PhoneIcon from "@material-ui/icons/PhoneOutlined";
import HistoryIcon from "@material-ui/icons/HistoryOutlined";
import SettingsIcon from "@material-ui/icons/SettingsOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PhonelinkRingIcon from "@material-ui/icons/PhonelinkRing";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/EditOutlined";
import StarIcon from "@material-ui/icons/StarOutline";
import StarFilledIcon from "@material-ui/icons/Star";
import LinkIcon from "@material-ui/icons/LinkOutlined";
import LampIcon from "@material-ui/icons/EmojiObjectsOutlined";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { connectToSocket } from "../../services/socket";
import { useConfirm } from "../../components/ConfirmDialog";
import { i18n } from "../../translate/i18n";
import { formatPhone } from "../../utils/formatPhone";
import ImportBrdidModal from "./ImportBrdidModal";
import ImportProgressModal from "./ImportProgressModal";
import brdidLogo from "../../assets/brdid-logo.png";
import NumberLinkDialog from "./NumberLinkDialog";
import Cdrs from "../Cdrs";

// verde de acento das abas ativas (mesmo tom do realce do menu; verde legível no
// branco — a lima da marca #BBFF00 seria ilegível como texto).
const TAB_GREEN = "#86C240";

// fonte do CORPO da página (tudo, exceto o título, que usa Space Grotesk)
const BODY_FONT = "Poppins, sans-serif";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg, fontFamily: BODY_FONT },
  wrap: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: theme.spacing(4, 3.5, 6) },

  // cabeçalho: título + subtítulo (esquerda) e ação primária (direita)
  head: { display: "flex", alignItems: "flex-start", gap: 16, marginBottom: theme.spacing(2.5) },
  headText: { flex: 1, minWidth: 0 },
  title: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 24, letterSpacing: "-0.01em", color: theme.app.ink, margin: 0 },
  subtitle: { fontFamily: "Poppins, sans-serif", fontFeatureSettings: "normal", fontSize: 14, color: theme.app.textSecondary, marginTop: 4 },
  importBtn: {
    flexShrink: 0,
    fontFamily: BODY_FONT,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    background: theme.app.dark,
    borderRadius: 8,
    padding: "9px 18px",
    boxShadow: "none",
    "&:hover": { background: theme.app.ink, boxShadow: "none" },
  },
  brdLogo: { height: 15, width: "auto", marginRight: 8, display: "block", filter: "brightness(0) invert(1)" },

  // abas Números | Chamadas (ícone + label; ativo em verde com underline verde)
  tabs: { display: "flex", alignItems: "center", gap: 28, marginBottom: theme.spacing(2.5) },
  tab: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    background: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 15.5,
    fontWeight: 600,
    color: theme.app.ink,
    padding: "4px 2px 11px",
    borderBottom: "2.5px solid transparent",
    "& svg": { fontSize: 19, color: theme.app.textTertiary },
    "&:hover": { color: TAB_GREEN, "& svg": { color: TAB_GREEN } },
  },
  tabOn: { color: TAB_GREEN, borderBottomColor: TAB_GREEN, "& svg": { color: TAB_GREEN } },
  tabCount: { fontFamily: BODY_FONT, fontSize: 11, fontWeight: 500, marginLeft: 4, opacity: 0.7 },

  // busca full-width (pill)
  search: { width: "100%", marginBottom: theme.spacing(2) },
  searchInput: { borderRadius: 999, background: "#fff", fontFamily: BODY_FONT },

  // pills de filtro
  pills: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: theme.spacing(2) },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 14px",
    borderRadius: 999,
    border: `1px solid ${theme.app.border}`,
    background: "#fff",
    color: theme.app.textSecondary,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { background: theme.app.hover },
  },
  pillOn: { background: theme.app.green, borderColor: theme.app.green, color: "#fff" },
  pillCount: { fontFamily: BODY_FONT, fontSize: 11, fontWeight: 500, opacity: 0.75 },

  // lista de cards
  list: { display: "flex", flexDirection: "column", gap: 10 },
  card: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: "#fff",
    border: `1px solid ${theme.app.border}`,
    borderRadius: 16,
    padding: "14px 18px",
    transition: "box-shadow .15s ease, border-color .15s ease",
    "&:hover": { boxShadow: "0 4px 16px rgba(27,34,42,.06)", borderColor: theme.app.borderInput },
    "&:hover $kebab": { opacity: 1 },
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 999,
    flexShrink: 0,
    background: "#EFF8D6",
    color: "#5f7a12",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 21 },
  },
  numCol: { width: 190, flexShrink: 0, minWidth: 0 },
  num: { fontFamily: BODY_FONT, fontSize: 14.5, fontWeight: 600, color: theme.app.ink, whiteSpace: "nowrap" },
  numLabel: { fontSize: 11.5, color: theme.app.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  numLabelRow: { display: "flex", alignItems: "center", gap: 3, marginTop: 1, minWidth: 0 },
  numLabelEmpty: { fontSize: 11.5, color: theme.app.textTertiary, fontStyle: "italic", opacity: 0.7, whiteSpace: "nowrap", cursor: "pointer" },
  editLabelBtn: { padding: 2, color: theme.app.textTertiary, "& svg": { fontSize: 13 }, "&:hover": { color: theme.app.ink } },
  defaultBadge: {
    display: "inline-flex", alignItems: "center", gap: 2, marginLeft: 8, verticalAlign: "middle",
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
    color: "#5f8f2a", background: theme.app.activeBg || "#EFF8D6", borderRadius: 999, padding: "1px 8px 1px 5px",
    "& svg": { fontSize: 12, color: "#86C240" },
  },
  // linha do NÚMERO DE TESTE compartilhado (dentro da lista)
  testAvatar: { background: "#EFF8D6", color: "#5f7a12" },
  selo: {
    display: "inline-flex", alignItems: "center", marginLeft: 8, verticalAlign: "middle",
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
    color: "#5f8f2a", background: "#EFF8D6", borderRadius: 999, padding: "1px 8px",
  },
  testLine: { display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 11, fontWeight: 500 },
  testDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  testing: { color: theme.app.textSecondary },
  testOk: { color: theme.app.ok || "#1f9d55" },
  testFail: { color: theme.app.danger || "#D9534F" },
  testPartial: { color: theme.app.warn || "#E0A92E" },

  // colunas de informação (rótulo pequeno + valor)
  infoCol: { minWidth: 0 },
  colType: { width: 92, flexShrink: 0 },
  colCity: { width: 150, flexShrink: 0 },
  colApp: { flex: 1, minWidth: 90 },
  infoLabel: { fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: theme.app.textTertiary, fontWeight: 600, marginBottom: 3 },
  infoVal: { fontSize: 13, fontWeight: 400, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  infoMuted: { color: theme.app.textTertiary },
  badge: { fontFamily: BODY_FONT, fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 500, display: "inline-block" },
  bFixo: { color: theme.app.textSecondary, background: theme.app.lineSoft },
  bMovel: { color: theme.app.info, background: "#EAF1F8" },

  // ações do card
  actions: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: "auto" },
  cfgBtn: {
    fontFamily: BODY_FONT,
    textTransform: "none",
    fontSize: 12.5,
    fontWeight: 600,
    color: theme.app.ink,
    background: "#fff",
    border: `1px solid ${theme.app.borderInput}`,
    borderRadius: 8,
    padding: "6px 15px",
    boxShadow: "none",
    "& svg": { fontSize: 16, marginRight: 6 },
    "&:hover": { background: theme.app.hover, boxShadow: "none" },
  },
  kebab: { color: theme.app.textTertiary, opacity: 0.55, transition: "opacity .12s", "&:hover": { color: theme.app.ink } },
  menuPaper: { minWidth: 176, borderRadius: 16, border: `1px solid ${theme.app.border}`, boxShadow: "0 12px 30px rgba(27,34,42,.14)", marginTop: 6 },
  menuItem: { fontFamily: BODY_FONT, fontSize: 13, gap: 10, padding: "8px 12px", "& svg": { fontSize: 18, color: theme.app.textTertiary } },
  menuDanger: { color: theme.app.danger || "#C0453B", "& svg": { color: theme.app.danger || "#C0453B" } },

  // empty state (card centralizado, estilo ref)
  empty: {
    background: "#fff",
    border: `1px solid ${theme.app.border}`,
    borderRadius: 16,
    padding: theme.spacing(7, 3),
    textAlign: "center",
  },
  emptyIcon: { width: 46, height: 46, borderRadius: 999, background: theme.app.lineSoft, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12, "& svg": { fontSize: 22, color: theme.app.textTertiary } },
  emptyText: { color: theme.app.textSecondary, fontSize: 13.5 },
}));

const Numbers = () => {
  const classes = useStyles();
  const [numbers, setNumbers] = useState([]);
  const [tab, setTab] = useState("numbers"); // numbers | chamadas
  const [filter, setFilter] = useState("all"); // all | fixo | movel
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [testEnabled, setTestEnabled] = useState(false);
  const [testingIds, setTestingIds] = useState({});
  const [progress, setProgress] = useState(null);
  const [menu, setMenu] = useState(null); // { anchor, number }
  const [testStatus, setTestStatus] = useState(null); // estado do número de teste (uso/amarração)
  const { confirm, prompt } = useConfirm();

  const testDigits = testStatus && testStatus.number ? String(testStatus.number).replace(/\D/g, "") : "";
  const isTestRow = n => !!testDigits && String(n.number).replace(/\D/g, "") === testDigits;
  const testAgents = applications.filter(a => a.triggerType === "agent");

  const fetchTestStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/numbers/test-number");
      setTestStatus(data && data.enabled ? data : null);
    } catch (err) {
      setTestStatus(null);
    }
  }, []);

  // amarra/desamarra o agente que o número de teste atende para este usuário
  const bindTestAgent = async applicationId => {
    try {
      if (!applicationId) await api.delete("/numbers/test-number/binding");
      else await api.put("/numbers/test-number/binding", { applicationId });
      fetchTestStatus();
    } catch (err) {
      toastError(err);
    }
  };

  // editar a etiqueta (nome livre) do número
  const renameLabel = async n => {
    const novo = await prompt({
      title: "Editar etiqueta",
      label: "Etiqueta",
      defaultValue: n.label || "",
      confirmText: "Salvar",
    });
    if (novo == null) return; // cancelou
    try {
      const { data } = await api.put(`/numbers/${n.id}`, { label: novo });
      setNumbers(list => list.map(x => (x.id === n.id ? data : x)));
    } catch (err) {
      toastError(err);
    }
  };

  const fetchNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/numbers", { params: { searchParam } });
      setNumbers(data.numbers);
      setTestEnabled(!!data.testEnabled);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam]);

  // define/remove o número PADRÃO (Caller ID usado quando a chamada não informa
  // origem — ex.: agente de voz via MCP). Um por conta.
  const setDefault = async n => {
    setMenu(null);
    try {
      await api.put(`/numbers/${n.id}`, { isDefault: !n.isDefault });
      fetchNumbers(); // marcar um zera os outros → recarrega p/ refletir
    } catch (err) {
      toastError(err);
    }
  };

  const handleTest = async n => {
    setMenu(null);
    setTestingIds(s => ({ ...s, [n.id]: true }));
    try {
      await api.post(`/numbers/${n.id}/test`);
    } catch (err) {
      toastError(err);
    } finally {
      setTestingIds(s => {
        const next = { ...s };
        delete next[n.id];
        return next;
      });
    }
  };

  const handleDelete = async n => {
    setMenu(null);
    const ok = await confirm({
      title: "Excluir número",
      message: `Excluir ${formatPhone(n.number)}? O número continuará configurado na BR DID mas deixará de funcionar.`,
      confirmText: "Excluir",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/numbers/${n.id}`);
      setNumbers(list => list.filter(x => x.id !== n.id));
    } catch (err) {
      toastError(err);
    }
  };

  // importar: abre o modal de progresso NA HORA (fechável) e a importação segue em
  // segundo plano — a resposta da BR DID pode levar dezenas de segundos. O teste
  // de cada número é disparado pelo backend ao fim da importação (roda mesmo se a
  // pessoa fechar a janela ou sair da página); o status aparece no card da lista.
  const startImport = async selected => {
    setProgress({ id: null, number: formatPhone(selected[0]), error: null });
    try {
      const { data } = await api.post("/numbers/brdid/import", { numbers: selected });
      fetchNumbers();
      const list = (data && data.imported) || [];
      if (!list.length) {
        setProgress(p => (p ? { ...p, error: "Nenhum número foi importado." } : p));
        return;
      }
      setProgress(p => (p ? { ...p, id: list[0].id, number: formatPhone(list[0].number) } : p));
    } catch (err) {
      setProgress(p => (p ? { ...p, error: "Não foi possível importar. Tente de novo." } : p));
      toastError(err);
    }
  };

  useEffect(() => {
    const delay = setTimeout(fetchNumbers, 300);
    return () => clearTimeout(delay);
  }, [fetchNumbers]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/applications");
        setApplications(data.applications);
      } catch (err) {
        toastError(err);
      }
    })();
    fetchTestStatus();
  }, [fetchTestStatus]);

  useEffect(() => {
    const socket = connectToSocket();
    const join = () => socket.emit("joinRoom", "numbers");
    socket.on("connect", join);
    if (socket.connected) join();
    socket.on("number", payload => {
      if (payload.action === "update") {
        setNumbers(list => list.map(n => (n.id === payload.number.id ? payload.number : n)));
      } else if (payload.action === "delete") {
        setNumbers(list => list.filter(n => n.id !== payload.id));
      }
    });
    return () => {
      socket.emit("leaveRoom", "numbers");
      socket.disconnect();
    };
  }, []);

  const handleSaved = updated => {
    setNumbers(list => list.map(n => (n.id === updated.id ? updated : n)));
  };

  // resumo visual do estado do teste do número (entrada + saída)
  const testInfo = n => {
    const s = n.testStatus;
    if (!s || s === "untested") return null;
    if (s === "testing") return { cls: classes.testing, dot: "#9aa3ad", label: "Em testes…", spin: true };
    const leg = (v, name) => (v === "ok" ? `${name} OK` : v === "fail" ? `${name} falhou` : `${name} —`);
    const detail = `${leg(n.testInbound, "Entrada")} · ${leg(n.testOutbound, "Saída")}`;
    if (s === "passed") return { cls: classes.testOk, dot: "#1f9d55", label: "Entrada e saída OK" };
    if (s === "partial") return { cls: classes.testPartial, dot: "#E0A92E", label: detail };
    return { cls: classes.testFail, dot: "#D9534F", label: detail };
  };

  const counts = {
    all: numbers.length,
    fixo: numbers.filter(n => n.type !== "movel").length,
    movel: numbers.filter(n => n.type === "movel").length,
  };
  const filtered = numbers.filter(n => {
    if (filter === "fixo") return n.type !== "movel";
    if (filter === "movel") return n.type === "movel";
    return true;
  });

  const pill = (key, label) => (
    <div className={clsx(classes.pill, filter === key && classes.pillOn)} onClick={() => setFilter(key)}>
      {label}
      <span className={classes.pillCount}>{counts[key]}</span>
    </div>
  );

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.head}>
          <div className={classes.headText}>
            <h1 className={classes.title}>{i18n.t("numbers.title")}</h1>
            <div className={classes.subtitle}>Gerencie os números de voz da sua conta.</div>
          </div>
          <Button className={classes.importBtn} onClick={() => setImportOpen(true)}>
            <img src={brdidLogo} alt="BR DID" className={classes.brdLogo} />
            Importar números
          </Button>
        </div>

        <div className={classes.tabs}>
          <button type="button" className={clsx(classes.tab, tab === "numbers" && classes.tabOn)} onClick={() => setTab("numbers")}>
            <PhoneIcon />
            {i18n.t("numbers.title")}
            <span className={classes.tabCount}>{numbers.length}</span>
          </button>
          <button type="button" className={clsx(classes.tab, tab === "chamadas" && classes.tabOn)} onClick={() => setTab("chamadas")}>
            <HistoryIcon />
            {i18n.t("menu.cdrs")}
          </button>
        </div>

        {tab === "chamadas" ? (
          <Cdrs embedded />
        ) : (
          <>
            <TextField
              className={classes.search}
              placeholder={i18n.t("numbers.search")}
              variant="outlined"
              size="small"
              value={searchParam}
              onChange={e => setSearchParam(e.target.value)}
              InputProps={{
                className: classes.searchInput,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.pills}>
              {pill("all", "Todos")}
              {pill("fixo", "Fixos")}
              {pill("movel", "Móveis")}
            </div>

            {!loading && filtered.length === 0 ? (
              <div className={classes.empty}>
                <div className={classes.emptyIcon}>
                  <PhoneIcon />
                </div>
                <div className={classes.emptyText}>{i18n.t("numbers.empty")}</div>
              </div>
            ) : (
              <div className={classes.list}>
                {filtered.map(n => {
                  // LINHA do número de teste compartilhado: linha normal + selo/uso;
                  // a especialização (verificar/agente) fica no diálogo Configurar.
                  if (isTestRow(n) && testStatus) {
                    const boundApp = testStatus.binding && testStatus.binding.application;
                    return (
                      <div key={n.id} className={classes.card}>
                        <div className={clsx(classes.avatar, classes.testAvatar)}>
                          <LampIcon />
                        </div>
                        <div className={classes.numCol}>
                          <div className={classes.num}>
                            {formatPhone(n.number)}
                            <span className={classes.selo}>Teste</span>
                          </div>
                        </div>

                        <div className={clsx(classes.infoCol, classes.colType)}>
                          <div className={classes.infoLabel}>{i18n.t("numbers.type")}</div>
                          <span className={clsx(classes.badge, n.type === "movel" ? classes.bMovel : classes.bFixo)}>
                            {i18n.t(`numbers.types.${n.type}`)}
                          </span>
                        </div>

                        <div className={clsx(classes.infoCol, classes.colCity)}>
                          <div className={classes.infoLabel}>{i18n.t("numbers.city")}</div>
                          <div className={clsx(classes.infoVal, !n.city && classes.infoMuted)}>{n.city || "—"}</div>
                        </div>

                        <div className={clsx(classes.infoCol, classes.colApp)}>
                          <div className={classes.infoLabel}>{i18n.t("numbers.inbound")}</div>
                          <div className={clsx(classes.infoVal, !boundApp && classes.infoMuted)}>
                            {boundApp ? boundApp.name : "—"}
                          </div>
                        </div>

                        <div className={classes.actions}>
                          <Button className={classes.cfgBtn} onClick={() => setEditing(n)}>
                            <SettingsIcon />
                            Configurar
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  const t = testInfo(n);
                  return (
                    <div key={n.id} className={classes.card}>
                      <div className={classes.avatar}>
                        <PhoneIcon />
                      </div>

                      <div className={classes.numCol}>
                        <div className={classes.num}>
                          {formatPhone(n.number)}
                          {n.isDefault && (
                            <span className={classes.defaultBadge}><StarFilledIcon />Padrão</span>
                          )}
                        </div>
                        <div className={classes.numLabelRow}>
                          {n.label ? (
                            <span className={classes.numLabel}>{n.label}</span>
                          ) : (
                            <span className={classes.numLabelEmpty} onClick={() => renameLabel(n)}>Adicionar etiqueta</span>
                          )}
                          <IconButton
                            size="small"
                            className={classes.editLabelBtn}
                            onClick={() => renameLabel(n)}
                            title={n.label ? "Editar etiqueta" : "Adicionar etiqueta"}
                          >
                            <EditIcon />
                          </IconButton>
                        </div>
                        {t && (
                          <div className={clsx(classes.testLine, t.cls)}>
                            {t.spin ? (
                              <CircularProgress size={9} thickness={6} style={{ color: t.dot }} />
                            ) : (
                              <span className={classes.testDot} style={{ background: t.dot }} />
                            )}
                            {t.label}
                          </div>
                        )}
                      </div>

                      <div className={clsx(classes.infoCol, classes.colType)}>
                        <div className={classes.infoLabel}>{i18n.t("numbers.type")}</div>
                        <span className={clsx(classes.badge, n.type === "movel" ? classes.bMovel : classes.bFixo)}>
                          {i18n.t(`numbers.types.${n.type}`)}
                        </span>
                      </div>

                      <div className={clsx(classes.infoCol, classes.colCity)}>
                        <div className={classes.infoLabel}>{i18n.t("numbers.city")}</div>
                        <div className={clsx(classes.infoVal, !n.city && classes.infoMuted)}>{n.city || "—"}</div>
                      </div>

                      <div className={clsx(classes.infoCol, classes.colApp)}>
                        <div className={classes.infoLabel}>{i18n.t("numbers.inbound")}</div>
                        <div className={clsx(classes.infoVal, !n.inboundApplication && classes.infoMuted)}>
                          {n.inboundApplication ? n.inboundApplication.name : "—"}
                        </div>
                      </div>

                      <div className={classes.actions}>
                        <Button className={classes.cfgBtn} onClick={() => setEditing(n)}>
                          <SettingsIcon />
                          Configurar
                        </Button>
                        {n.ownerId && (
                          <IconButton
                            size="small"
                            className={classes.kebab}
                            onClick={e => setMenu({ anchor: e.currentTarget, number: n })}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <Menu
        anchorEl={menu && menu.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <MenuItem className={classes.menuItem} onClick={() => { const n = menu.number; setMenu(null); setEditing(n); }}>
          <LinkIcon fontSize="small" />
          {i18n.t("numbers.link")}
        </MenuItem>
        {testEnabled && menu && menu.number.ownerId && (
          <MenuItem
            className={classes.menuItem}
            disabled={!!(menu && testingIds[menu.number.id]) || (menu && menu.number.testStatus === "testing")}
            onClick={() => handleTest(menu.number)}
          >
            <PhonelinkRingIcon fontSize="small" />
            Testar número
          </MenuItem>
        )}
        <MenuItem className={classes.menuItem} onClick={() => setDefault(menu.number)}>
          {menu && menu.number.isDefault ? <StarFilledIcon fontSize="small" /> : <StarIcon fontSize="small" />}
          {menu && menu.number.isDefault ? "Remover padrão" : "Definir como padrão"}
        </MenuItem>
        <MenuItem className={clsx(classes.menuItem, classes.menuDanger)} onClick={() => handleDelete(menu.number)}>
          <DeleteIcon fontSize="small" />
          Excluir número
        </MenuItem>
      </Menu>

      <NumberLinkDialog
        open={!!editing}
        number={editing}
        applications={applications}
        testStatus={editing && isTestRow(editing) ? testStatus : null}
        agents={testAgents}
        onTestBind={bindTestAgent}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />

      <ImportBrdidModal open={importOpen} onClose={() => setImportOpen(false)} onImport={startImport} />

      <ImportProgressModal
        open={!!progress}
        label={progress && progress.number}
        error={progress && progress.error}
        num={progress && progress.id ? numbers.find(n => n.id === progress.id) : null}
        onClose={() => setProgress(null)}
      />
    </div>
  );
};

export default Numbers;
